<template lang="pug">
.scheduled-message-list-table-component
  b-table(
    :items="_value"
    :fields="fields"
    :current-page="currentPage"
    :per-page="perPage"
    :filter="filter"
    :busy="isLoading"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
    :filter-function="filterTable"
    @filtered="onFiltered"
  )
    // ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    // レコード無し
    template(#empty)
      .empty-message
        span 情報がありません
    // フィルター後レコード無し
    template(#emptyfiltered)
      .empty-message
        span 情報がありません

    // セル：送信予定日時
    template(#cell(scheduledTime)="row")
      span {{ getDisplayDate(row.item) }}
    // セル：送信種別
    template(#cell(type)="row")
      span {{ getDisplayType(row.item.type) }}
    // セル：タイトル
    template(#cell(title)="row")
      .overflow-hidden.title(:id="`title-${row.item.sortKey}`") {{ row.item.functionContents.title }}
      b-popover(v-if="row.item.functionContents.title" :target="`title-${row.item.sortKey}`" triggers="hover" :placement="'bottomright'")
        .d-flex.flex-column
          span {{ `${row.item.functionContents.title}` }}
    // セル：送信者
    template(#cell(writtenBy)="row")
      span {{ row.item.writtenBy }}
    // セル：操作
    template(#cell(operation)="row")
      button.btn.confirm-btn.mr-2(@click="onConfirm(row.item, $event)") 確認
      //- button.btn.confirm-btn.mr-2(v-if="row.item.status !== 'sent'" @click="onChangeTime(row.item)") 日時変更
      button.btn.confirm-btn.mr-2(@click="onChangeTime(row.item)") 日時変更
      //- button.btn.delete-btn(v-if="row.item.status !== 'sent'" @click="onDelete(row.item)") 取消し
      button.btn.delete-btn(@click="onDelete(row.item)") 取消し
</template>

<script lang="ts">
import { SendMessageFunction } from "@/commons/models/Message";
import { ReceiverDetail } from "@/commons/models/SendLineMessage";
import { Component, Prop, Vue } from "vue-property-decorator";
import { Type, Status } from "@/commons/services/ScheduledMessages";

/**
 * 送信予約メッセージテーブルコンポーネント
 */
@Component({})
export default class ScheduledMessageListTable extends Vue {
  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<ScheduledMessageTableItem>;
  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<ScheduledMessageTableField>;
  /** 全体行数 */
  @Prop({ required: true })
  totalRows!: number;
  /** 現在のページ */
  @Prop({ required: true })
  currentPage!: number;
  /** 1ページあたりの表示行数 */
  @Prop({ required: true })
  perPage!: number;
  /** ローディング表示可否 */
  @Prop({ required: true })
  isLoading!: boolean;
  /** フィルター */
  @Prop()
  filter!: FilterSetting;

  get _value(): Array<ScheduledMessageTableItem> {
    return this.value;
  }

  /**
   * ### フィルタリング
   */
  public filterTable(row: ScheduledMessageTableItem, filter: FilterSetting): boolean {
    return this.filterSearchWords(row, filter);
  }

  public filterSearchWords(row: ScheduledMessageTableItem, filter: FilterSetting): boolean {
    if (filter.searchWord == "") {
      return true;
    }

    const words: string[] = this.$filterUtil.createSearchWord(filter.searchWord);

    let foundReceivers: boolean = true;
    let foundTitle: boolean = true;

    for (const word of words) {
      // 宛先
      if (row.receivers.indexOf(word) == -1) {
        foundReceivers = false;
      }

      // タイトル
      if (row.functionContents.title.indexOf(word) == -1) {
        foundTitle = false;
      }
    }

    return foundReceivers || foundTitle;
  }

  /**
   * ### フィルタリングイベント
   */
  public onFiltered(filteredItems: any) {
    this.$emit("update:totalRows", filteredItems.length);
    return;
  }

  /**
   * ### 画面表示用の日付を返す
   */
  public getDisplayDate(message: ScheduledMessageTableItem): string {
    if (message.scheduledTime) {
      return this.$dateUtil.convertNumberToString(Number(message.scheduledTime), "mm月dd日 hh:MM");
    } else {
      return "";
    }
  }

  /**
   * ### 送信種別の日本語名を返す
   */
  public getDisplayType(type: Type | string): string {
    switch (type) {
      case "allFamilies":
      case "0":
        return "家族全員";
      case "families":
      case "1":
        return "家族個別";
      case "otherUserGroupMembers":
      case "2":
        return "施設関係者個別";
      case "sendMessageGroupsForFamilies":
      case "3":
        return "宛先グループ（家族）";
      case "sendMessageGroupsForOtherUserGroups":
        return "宛先グループ（施設関係者）";
      case "allWaiters":
      case "4":
        return "入所申込者全員";
      case "waiters":
      case "5":
        return "入所申込者個別";
      case "residentGroups":
      case "6":
        return "利用者グループ";
      case "otherUserGroups":
      case "7":
        return "施設関係者グループ";
      default:
        return "";
    }
  }

  /**
   * ### ステータスの日本語名を返す
   */
  public getDisplayStatus(status: Status): string {
    switch (status) {
      case "scheduled":
        return "予約中";
      case "sent":
        return "送信済み";
      case "failed":
        return "送信失敗";
      case "canceled":
        return "キャンセル";
      default:
        return "";
    }
  }

  /**
   * ### 確認ボタン押下イベント
   */
  public onConfirm(item: ScheduledMessageTableItem, event?: Event): void {
    if (event && event.currentTarget instanceof HTMLElement) {
      event.currentTarget.blur();
    }
    this.$emit("onConfirm", item);
    return;
  }

  /**
   * ### 日時変更ボタン押下イベント
   */
  public onChangeTime(item: ScheduledMessageTableItem): void {
    this.$emit("onChangeTime", item);
    return;
  }

  /**
   * ### 削除ボタン押下イベント
   */
  public onDelete(item: ScheduledMessageTableItem): void {
    this.$emit("onDelete", item);
    return;
  }
}

/** テーブルの列名の情報 */
export interface ScheduledMessageTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface ScheduledMessageTableItem {
  facilityId: number;
  sortKey: string;
  scheduledTime: number;
  type: Type;
  status: Status;
  receivers: string;
  functionContents: SendMessageFunction;
  writerId: string;
  writtenBy: string;
  fileUrls: string[];
  fileNames: string[];
  files?: Array<File>;
  receiverDetails?: Array<ReceiverDetail>;
  payload: any;
  updated: number;
}

export interface FilterSetting {
  /** 検索ワード */
  searchWord: string;
}
</script>

<style lang="scss" scoped>
.send-message-history-table-component {
  .confirm-btn {
    width: 64px;
  }
  // 文字がtd要素の幅をはみ出した場合「…」で隠すスタイル
  .overflow-hidden {
    overflow: hidden !important;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
